import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { RefreshCw, TrendingUp, TrendingDown, ShoppingBag, Smartphone, Globe, MapPin, Clock } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#0C63BF', '#32CD32', '#7c3aed', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#6366f1'];
const SOURCE_COLORS = {
  direct: '#0C63BF', google: '#4285F4', bing: '#00897B', yahoo: '#5F01D1',
  duckduckgo: '#DE5833', instagram: '#E1306C', facebook: '#1877F2',
  tiktok: '#000000', twitter: '#1DA1F2', linkedin: '#0A66C2',
  youtube: '#FF0000', whatsapp: '#25D366', reddit: '#FF4500',
  internal: '#5A8BA8', other: '#94A3B8',
};

const DEVICE_COLORS = { mobile: '#0C63BF', desktop: '#32CD32', tablet: '#f59e0b', unknown: '#94A3B8' };
const ccToFlag = (cc) => cc ? String.fromCodePoint(...[...cc.toUpperCase()].map(c => 127397 + c.charCodeAt(0))) : '';

const StatsTab = () => {
  const [stats, setStats] = useState(null);
  const [enhanced, setEnhanced] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartWeeks, setChartWeeks] = useState(12);
  const [analytics, setAnalytics] = useState(null);
  const [visitsPeriod, setVisitsPeriod] = useState('week');
  const [sourceFilter, setSourceFilter] = useState('all');

  useEffect(() => {
    adminAPI.getStats().then(r => setStats(r.data)).catch(() => {});
    adminAPI.getEnhancedStats().then(r => setEnhanced(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    adminAPI.getAnalytics(sourceFilter).then(r => setAnalytics(r.data)).catch(() => {});
  }, [sourceFilter]);
  useEffect(() => { adminAPI.getChartData(chartWeeks).then(r => setChartData(r.data.chart_data || [])).catch(() => {}); }, [chartWeeks]);

  if (!stats) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-[#0C63BF]" /></div>;

  const cards = [
    { label: 'Utilisateurs', value: stats.total_users, color: 'text-[#0C63BF]' },
    { label: 'Annonces actives', value: stats.active_listings, color: 'text-[#32CD32]' },
    { label: 'Vendues', value: stats.sold_listings, color: 'text-purple-600' },
    { label: 'Commandes', value: stats.paid_orders, color: 'text-[#033871]' },
    { label: 'Volume total', value: stats.total_volume.toFixed(2) + ' EUR', color: 'text-[#033871]' },
    { label: 'Frais protection', value: stats.total_protection_fees.toFixed(2) + ' EUR', color: 'text-[#32CD32]' },
    { label: 'Conversion', value: enhanced ? enhanced.conversion_rate + '%' : '-', color: 'text-amber-600' },
    { label: 'Panier moyen', value: enhanced ? enhanced.avg_order_value + ' EUR' : '-', color: 'text-[#0C63BF]' },
  ];

  const visitsData = analytics ? (visitsPeriod === 'week' ? (analytics.chart_weekly || []) : (analytics.chart_monthly || [])) : [];
  const visitsTrend = analytics ? (visitsPeriod === 'week' ? (analytics.week && analytics.week.trend) : (analytics.month && analytics.month.trend)) : 0;
  const trendSafe = visitsTrend || 0;
  const sourcesBreakdown = (analytics && analytics.sources_breakdown) || [];
  const totalSourceCount = sourcesBreakdown.reduce((s, x) => s + x.count, 0) || 1;
  const availableSources = (analytics && analytics.available_sources) || [];
  const activeSources = ['all', ...sourcesBreakdown.map(s => s.source)];
  const sourceOptions = availableSources.filter(o => activeSources.indexOf(o.value) !== -1);
  const devicesBreakdown = (analytics && analytics.devices_breakdown) || [];
  const totalDeviceCount = devicesBreakdown.reduce((s, x) => s + x.count, 0) || 1;
  const topCountries = (analytics && analytics.top_countries) || [];
  const topCities = (analytics && analytics.top_cities) || [];
  const maxCountry = (topCountries[0] && topCountries[0].count) || 1;
  const maxCity = (topCities[0] && topCities[0].count) || 1;
  const hourlyChart = (analytics && analytics.hourly_chart) || [];
  const peakHour = hourlyChart.length ? hourlyChart.reduce((a, b) => (b.visits > a.visits ? b : a)) : null;

  return (
    <div data-testid="admin-stats">
      {analytics && (
        <div className="bg-gradient-to-r from-[#0C63BF] to-[#033871] rounded-xl p-4 mb-6 text-white" data-testid="site-visits">
          <h2 className="text-base font-bold mb-3">Visites du site {sourceFilter !== 'all' && <span className="text-xs font-normal text-white/70">— filtré : {sourceFilter}</span>}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-3xl font-black">{analytics.today.visits}</p>
              <p className="text-xs text-white/70">Aujourd hui</p>
            </div>
            <div>
              <p className="text-3xl font-black">{analytics.today.unique}</p>
              <p className="text-xs text-white/70">Visiteurs uniques</p>
            </div>
            <div>
              <p className="text-3xl font-black">{analytics.week.visits}</p>
              <p className="text-xs text-white/70">Cette semaine</p>
            </div>
            <div>
              <p className="text-3xl font-black">{analytics.month.visits}</p>
              <p className="text-xs text-white/70">Ce mois</p>
            </div>
          </div>
          {analytics.top_pages && analytics.top_pages.length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/20">
              <p className="text-xs text-white/70 mb-2">Pages populaires</p>
              <div className="flex flex-wrap gap-2">
                {analytics.top_pages.slice(0, 5).map((p, i) => (
                  <span key={i} className="px-2 py-1 bg-white/10 rounded text-xs">{p.page} ({p.count})</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sources breakdown */}
      {analytics && sourcesBreakdown.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E8F4FD] p-4 mb-6" data-testid="sources-breakdown">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-[#033871]">Sources de trafic <span className="text-xs font-normal text-[#5A8BA8]">(30 derniers jours)</span></h3>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Bar list */}
            <div className="space-y-2">
              {sourcesBreakdown.map(s => {
                const pct = Math.round((s.count / totalSourceCount) * 100);
                const color = SOURCE_COLORS[s.source] || '#94A3B8';
                return (
                  <div key={s.source} className="flex items-center gap-2.5" data-testid={`source-row-${s.source}`}>
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
                    <span className="text-xs font-medium text-[#033871] w-24 shrink-0">{s.label}</span>
                    <div className="flex-1 h-2 bg-[#F0F7FA] rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: pct + '%', background: color }} />
                    </div>
                    <span className="text-xs font-bold text-[#0C63BF] w-12 text-right">{s.count}</span>
                    <span className="text-[10px] text-[#5A8BA8] w-10 text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
            {/* Donut */}
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={sourcesBreakdown} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={2}>
                  {sourcesBreakdown.map((s, i) => <Cell key={i} fill={SOURCE_COLORS[s.source] || COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Devices & Geography */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6" data-testid="devices-geo-section">
          <div className="bg-white rounded-xl border border-[#E8F4FD] p-4" data-testid="devices-breakdown">
            <h3 className="text-sm font-bold text-[#033871] mb-3 flex items-center gap-1.5"><Smartphone className="w-4 h-4" /> Appareils <span className="text-xs font-normal text-[#5A8BA8]">(30 jours)</span></h3>
            {devicesBreakdown.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart>
                    <Pie data={devicesBreakdown} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={38} outerRadius={58} paddingAngle={2}>
                      {devicesBreakdown.map((d, i) => <Cell key={i} fill={DEVICE_COLORS[d.device] || COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-2">
                  {devicesBreakdown.map(d => {
                    const pct = Math.round((d.count / totalDeviceCount) * 100);
                    return (
                      <div key={d.device} className="flex items-center gap-2" data-testid={`device-row-${d.device}`}>
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: DEVICE_COLORS[d.device] || '#94A3B8' }} />
                        <span className="text-xs font-medium text-[#033871] flex-1">{d.label}</span>
                        <span className="text-xs font-bold text-[#0C63BF]">{d.count}</span>
                        <span className="text-[10px] text-[#5A8BA8] w-9 text-right">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : <p className="text-xs text-[#5A8BA8] text-center py-8">Collecte en cours — les données apparaîtront avec les prochaines visites</p>}
          </div>
          <div className="bg-white rounded-xl border border-[#E8F4FD] p-4" data-testid="top-countries">
            <h3 className="text-sm font-bold text-[#033871] mb-3 flex items-center gap-1.5"><Globe className="w-4 h-4" /> Top pays <span className="text-xs font-normal text-[#5A8BA8]">(30 jours)</span></h3>
            {topCountries.length > 0 ? (
              <div className="space-y-2">
                {topCountries.map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`country-row-${i}`}>
                    <span className="text-base w-6 text-center shrink-0">{ccToFlag(c.country_code)}</span>
                    <span className="text-xs font-medium text-[#033871] w-24 truncate shrink-0">{c.country}</span>
                    <div className="flex-1 h-2 bg-[#F0F7FA] rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-[#0C63BF]" style={{ width: Math.round((c.count / maxCountry) * 100) + '%' }} />
                    </div>
                    <span className="text-xs font-bold text-[#0C63BF] w-8 text-right">{c.count}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-[#5A8BA8] text-center py-8">Collecte en cours — les données apparaîtront avec les prochaines visites</p>}
          </div>
          <div className="bg-white rounded-xl border border-[#E8F4FD] p-4" data-testid="top-cities">
            <h3 className="text-sm font-bold text-[#033871] mb-3 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> Top villes <span className="text-xs font-normal text-[#5A8BA8]">(30 jours)</span></h3>
            {topCities.length > 0 ? (
              <div className="space-y-2">
                {topCities.map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`city-row-${i}`}>
                    <span className="text-base w-6 text-center shrink-0">{ccToFlag(c.country_code)}</span>
                    <span className="text-xs font-medium text-[#033871] w-24 truncate shrink-0">{c.city}</span>
                    <div className="flex-1 h-2 bg-[#F0F7FA] rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-[#32CD32]" style={{ width: Math.round((c.count / maxCity) * 100) + '%' }} />
                    </div>
                    <span className="text-xs font-bold text-[#0C63BF] w-8 text-right">{c.count}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-[#5A8BA8] text-center py-8">Collecte en cours — les données apparaîtront avec les prochaines visites</p>}
          </div>
        </div>
      )}

      {/* Hourly peaks */}
      {analytics && hourlyChart.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E8F4FD] p-4 mb-6" data-testid="hourly-peaks">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-bold text-[#033871] flex items-center gap-1.5"><Clock className="w-4 h-4" /> Heures de pointe <span className="text-xs font-normal text-[#5A8BA8]">(30 jours, heure de Paris)</span></h3>
            {peakHour && peakHour.visits > 0 && (
              <span className="text-xs font-bold text-[#0C63BF] bg-[#E8F4FD] px-2.5 py-1 rounded-lg" data-testid="peak-hour-badge">Pic : {peakHour.hour} ({peakHour.visits} visites)</span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={hourlyChart} barGap={1}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8F4FD" />
              <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#5A8BA8' }} interval={1} />
              <YAxis tick={{ fontSize: 10, fill: '#5A8BA8' }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E8F4FD', fontSize: 12 }} />
              <Bar dataKey="visits" name="Visites" radius={[3, 3, 0, 0]}>
                {hourlyChart.map((h, i) => <Cell key={i} fill={peakHour && h.hour === peakHour.hour ? '#32CD32' : '#0C63BF'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {analytics && (
        <div className="bg-white rounded-xl border border-[#E8F4FD] p-4 mb-6" data-testid="visits-evolution">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <h3 className="text-sm font-bold text-[#033871]">Evolution des visites</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-xs text-[#5A8BA8]">
                  {visitsPeriod === 'week' ? '12 dernieres semaines' : '12 derniers mois'}
                </p>
                {trendSafe !== 0 && (
                  <span className={'inline-flex items-center gap-0.5 text-[11px] font-bold ' + (trendSafe > 0 ? 'text-green-600' : 'text-red-500')}>
                    {trendSafe > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {trendSafe > 0 ? '+' : ''}{trendSafe}%
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="text-xs border border-[#D6E8F0] rounded-lg px-2 py-1.5 bg-white text-[#033871] font-medium focus:outline-none focus:ring-2 focus:ring-[#0C63BF]/20"
                data-testid="source-filter-select"
              >
                {sourceOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <div className="flex gap-1" data-testid="visits-period-toggle">
                <button
                  onClick={() => setVisitsPeriod('week')}
                  className={'px-3 py-1 rounded-lg text-xs font-medium ' + (visitsPeriod === 'week' ? 'bg-[#0C63BF] text-white' : 'bg-[#F0F7FA] text-[#5A8BA8] hover:bg-[#E8F4FD]')}
                  data-testid="visits-week-btn"
                >Par semaine</button>
                <button
                  onClick={() => setVisitsPeriod('month')}
                  className={'px-3 py-1 rounded-lg text-xs font-medium ' + (visitsPeriod === 'month' ? 'bg-[#0C63BF] text-white' : 'bg-[#F0F7FA] text-[#5A8BA8] hover:bg-[#E8F4FD]')}
                  data-testid="visits-month-btn"
                >Par mois</button>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={visitsData}>
              <defs>
                <linearGradient id="visitsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0C63BF" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#0C63BF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="uniqueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#32CD32" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#32CD32" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8F4FD" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#5A8BA8' }} />
              <YAxis tick={{ fontSize: 10, fill: '#5A8BA8' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: '1px solid #E8F4FD', fontSize: 12 }}
                labelFormatter={(label, items) => {
                  const item = items && items[0] && items[0].payload;
                  return item && item.period ? item.period : label;
                }}
              />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="visits" name="Visites" stroke="#0C63BF" strokeWidth={2} fill="url(#visitsGradient)" />
              <Area type="monotone" dataKey="unique" name="Visiteurs uniques" stroke="#32CD32" strokeWidth={2} fill="url(#uniqueGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {cards.map(c => (
          <div key={c.label} className="bg-white rounded-xl border border-[#E8F4FD] p-4">
            <p className="text-xs text-[#5A8BA8] mb-1">{c.label}</p>
            <p className={'text-2xl font-black ' + c.color}>{c.value}</p>
          </div>
        ))}
      </div>

      {enhanced && enhanced.recent_activity && (
        <div className="bg-gradient-to-r from-[#033871] to-[#0C63BF] rounded-xl p-4 mb-6 text-white">
          <p className="text-xs font-medium text-white/70 mb-2">7 derniers jours</p>
          <div className="grid grid-cols-3 gap-4">
            <div><p className="text-2xl font-black">{enhanced.recent_activity.users}</p><p className="text-xs text-white/70">Inscriptions</p></div>
            <div><p className="text-2xl font-black">{enhanced.recent_activity.listings}</p><p className="text-xs text-white/70">Annonces</p></div>
            <div><p className="text-2xl font-black">{enhanced.recent_activity.orders}</p><p className="text-xs text-white/70">Commandes</p></div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-[#033871]">Evolution</h2>
        <div className="flex gap-1">
          {[4, 8, 12, 24].map(w => (
            <button key={w} onClick={() => setChartWeeks(w)} className={'px-2.5 py-1 rounded-lg text-xs font-medium ' + (chartWeeks === w ? 'bg-[#0C63BF] text-white' : 'bg-white border border-[#D6E8F0] text-[#5A8BA8]')}>{w}s</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-[#E8F4FD] p-4">
          <h3 className="text-sm font-semibold text-[#033871] mb-3">Activite</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8F4FD" />
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#5A8BA8' }} />
              <YAxis tick={{ fontSize: 10, fill: '#5A8BA8' }} allowDecimals={false} />
              <Tooltip />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="inscriptions" name="Inscriptions" fill="#0C63BF" radius={[3, 3, 0, 0]} />
              <Bar dataKey="annonces" name="Annonces" fill="#32CD32" radius={[3, 3, 0, 0]} />
              <Bar dataKey="ventes" name="Ventes" fill="#7c3aed" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-[#E8F4FD] p-4">
          <h3 className="text-sm font-semibold text-[#033871] mb-3">Volume</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <defs><linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0C63BF" stopOpacity={0.3}/><stop offset="95%" stopColor="#0C63BF" stopOpacity={0}/></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8F4FD" />
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#5A8BA8' }} />
              <YAxis tick={{ fontSize: 10, fill: '#5A8BA8' }} />
              <Tooltip />
              <Area type="monotone" dataKey="volume" name="Volume" stroke="#0C63BF" strokeWidth={2} fill="url(#volGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {enhanced && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-[#E8F4FD] p-4">
            <h3 className="text-sm font-semibold text-[#033871] mb-3">Top marques</h3>
            {enhanced.top_brands && enhanced.top_brands.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart><Pie data={enhanced.top_brands} dataKey="count" nameKey="brand" cx="50%" cy="50%" outerRadius={70} labelLine={false}>{enhanced.top_brands.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-[#5A8BA8] text-center py-8">Aucune donnee</p>}
          </div>
          <div className="bg-white rounded-xl border border-[#E8F4FD] p-4">
            <h3 className="text-sm font-semibold text-[#033871] mb-3 flex items-center gap-1.5"><TrendingUp className="w-4 h-4" /> Top vendeurs</h3>
            {enhanced.top_sellers && enhanced.top_sellers.length > 0 ? (
              <div className="space-y-2.5">{enhanced.top_sellers.map((s, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <span className={'w-6 h-6 rounded-lg flex items-center justify-center text-white font-bold text-xs ' + (i === 0 ? 'bg-amber-400' : i === 1 ? 'bg-gray-400' : 'bg-[#D6E8F0]')}>{i + 1}</span>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[#033871] truncate">{s.name}</p><p className="text-[10px] text-[#5A8BA8]">{s.sales} ventes</p></div>
                  <span className="text-sm font-bold text-[#0C63BF]">{s.volume} EUR</span>
                </div>
              ))}</div>
            ) : <p className="text-sm text-[#5A8BA8] text-center py-8">Aucune vente</p>}
          </div>
          <div className="bg-white rounded-xl border border-[#E8F4FD] p-4">
            <h3 className="text-sm font-semibold text-[#033871] mb-3 flex items-center gap-1.5"><ShoppingBag className="w-4 h-4" /> Dernieres ventes</h3>
            {enhanced.recent_orders && enhanced.recent_orders.length > 0 ? (
              <div className="space-y-2.5">{enhanced.recent_orders.map((o, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[#033871] truncate">{o.brand} {o.model}</p><p className="text-[10px] text-[#5A8BA8]">{o.buyer_name}</p></div>
                  <span className="text-sm font-bold text-[#32CD32]">{o.total ? o.total.toFixed(0) : 0} EUR</span>
                </div>
              ))}</div>
            ) : <p className="text-sm text-[#5A8BA8] text-center py-8">Aucune vente</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsTab;
